import { ReactNode } from 'react';
import { Machine as GeneratedMachine } from '../../gql/generated';
import { InfoRow as PanelRow, InfoSectionHeader } from '../SidePanel';

// Use the generated Machine type from GraphQL
type Machine = GeneratedMachine;

interface MachineDetailsTabProps {
  machine: Machine;
  /**
   * `panel` (the default) lists the rows under shaded headers, as every side
   * panel does. `cards` sets each section on a card, for the phone and tablet
   * detail page.
   */
  appearance?: 'panel' | 'cards';
}

interface InfoSectionProps {
  title: string;
  children: ReactNode;
}

/** Opens the container query the rows read to pick their density. */
function PanelSection({ title, children }: InfoSectionProps) {
  return (
    <section className='@container'>
      <InfoSectionHeader>{title}</InfoSectionHeader>
      <div
        className='@[36rem]:divide-border-secondary flex flex-col gap-2
          px-(--info-gutter) py-4 @[36rem]:gap-0 @[36rem]:divide-y
          @[36rem]:py-0'
      >
        {children}
      </div>
    </section>
  );
}

/** A section as a card under a small capitals title. */
function CardSection({ title, children }: InfoSectionProps) {
  return (
    <section className='flex flex-col gap-3'>
      <h3 className='size-caption text-primary tracking-[0.08em] uppercase'>
        {title}
      </h3>
      <div
        className='bg-surface-primary border-border-secondary
          divide-border-secondary flex flex-col divide-y rounded-xl border px-4
          shadow-[0_4px_12px_rgba(0,0,0,0.03)]'
      >
        {children}
      </div>
    </section>
  );
}

/** A card row: the label over its value. */
function CardRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className='flex flex-col gap-1 py-4'>
      <span className='size-caption text-secondary'>{label}</span>
      <span className='size-p2 text-primary wrap-break-word'>
        {value || 'Not available'}
      </span>
    </div>
  );
}

/**
 * EDMS date-times arrive as ISO-8601 UTC strings (the DateTimeISO scalar is
 * untyped), but tolerate a Date in case a type policy ever parses them. Kept
 * verbatim rather than reformatted, so the value matches the raw EDMS response.
 */
function isoString(value: unknown): string {
  if (!value) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  return String(value);
}

/**
 * MachineDetailsTab - Displays machine details in a side panel format
 *
 * Shows Basic Information, Customer Information, Plant Information,
 * and Technical Information in a compact list layout.
 */
export function MachineDetailsTab({
  machine,
  appearance = 'panel',
}: MachineDetailsTabProps) {
  const cards = appearance === 'cards';
  const InfoSection = cards ? CardSection : PanelSection;
  const InfoRow = cards ? CardRow : PanelRow;

  return (
    <div className={cards ? 'flex flex-col gap-8' : 'flex flex-col'}>
      <InfoSection title='Basic information'>
        <InfoRow label='Machine ID' value={machine.machineId} />
        <InfoRow
          label='Display Name'
          value={machine.displayName || machine.machineId}
        />
        <InfoRow label='Serial number' value={machine.serial} />
        <InfoRow label='Part Number' value={machine.partNumber} />
        <InfoRow label='SOID' value={machine.soid} />
        <InfoRow label='Family' value={machine.family} />
      </InfoSection>

      {/* The raw EDMS MachineStateDto fields behind the derived Status column,
          labelled with the EDMS field names so a machine can be compared
          directly against GET /api/machines. Always rendered: EDMS returns no
          state at all for a machine that has never reported, and that absence
          is exactly what someone opening this panel needs to see. */}
      <InfoSection title='Machine state (EDMS)'>
        {machine.state ? (
          <>
            <InfoRow label='timeUtc' value={isoString(machine.state.timeUtc)} />
            <InfoRow
              label='isSynthetic'
              value={
                machine.state.isSynthetic == null
                  ? ''
                  : String(machine.state.isSynthetic)
              }
            />
            <InfoRow
              label='lastRecordedState'
              value={machine.state.lastRecordedState}
            />
            <InfoRow
              label='lastRecordedStateTimeUtc'
              value={isoString(machine.state.lastRecordedStateTimeUtc)}
            />
          </>
        ) : (
          <InfoRow label='State' value='No state received from EDMS' />
        )}
      </InfoSection>

      {/* Always rendered: missing data shows as empty rows rather than a
          silently dropped section */}
      <InfoSection title='Customer Information'>
        <InfoRow label='Customer ID' value={machine.customer?.id} />
        <InfoRow label='IFS ID' value={machine.customer?.ifsId} />
        <InfoRow label='Name' value={machine.customer?.name} />
        <InfoRow label='Display Name' value={machine.customer?.displayName} />
        <InfoRow label='Country' value={machine.customer?.country} />
        <InfoRow
          label='Representative'
          value={machine.customer?.representative}
        />
        <InfoRow label='Category' value={machine.customer?.customerCategory} />
      </InfoSection>

      <InfoSection title='Plant Information'>
        <InfoRow label='Plant ID' value={machine.plant?.id} />
        <InfoRow label='Plant Name' value={machine.plant?.displayName} />
        <InfoRow label='Country' value={machine.address?.country} />
        <InfoRow label='Country code' value={machine.address?.countryCode} />
      </InfoSection>

      {/* Always rendered: a machine without an edge device should say so rather
          than silently drop the section */}
      <InfoSection title='Edge Device Information'>
        {machine.edgeDevice ? (
          <>
            <InfoRow
              label='Edge Device ID (IoT)'
              value={machine.edgeDevice.deviceId}
            />
            <InfoRow
              label='Edge Device Name'
              value={machine.edgeDevice.displayName}
            />
            <InfoRow label='Edge Device DB ID' value={machine.edgeDevice.id} />
          </>
        ) : (
          <InfoRow label='Edge Device' value='Not provisioned' />
        )}
      </InfoSection>

      <InfoSection title='Technical Information'>
        <InfoRow label='Internal ID' value={machine.id} />
        <InfoRow label='Plant Name' value={machine.plant?.displayName} />
        <InfoRow label='Country' value={machine.address?.country} />
      </InfoSection>
    </div>
  );
}
